import { isMobile } from 'react-device-detect';
import { useState } from 'react';
import { useAppDispatch, useAppContext } from '../../../../providers';
import { RESOURCE_QUEUE_SELECTED_ID } from '../../../../providers/AppProvider/types';
import {
  useQueues,
  useResources,
  useUpdateResourceQueue,
  useCreateResourceQueue,
  useDeleteResourceQueue,
} from '../../../../hooks';

import { Drawer, DrawerContent, Button, Select } from '@pella/sash-framework';
import { SetupCards } from '../../SetupCards/SetupCards';

import * as classes from './ResourceQueuesDrawer.module.css';

/**
 * Component for the ResourceQueues Drawer.
 * @param {Local.ResourceQueueDrawerProps} props - Component props
 */
export const ResourceQueuesDrawer = ({ data, type }) => {
  const dispatch = useAppDispatch();
  const { selectedPlant } = useAppContext();

  const { data: queues } = useQueues(selectedPlant, false);
  const { data: resources } = useResources(false);

  const { mutate: updateResourceQueue } = useUpdateResourceQueue();
  const { mutate: createResourceQueue } = useCreateResourceQueue();
  const { mutate: deleteResourceQueue } = useDeleteResourceQueue();

  const [selection, setSelection] = useState('');

  const selectData = type === 'queue' ? queues : resources;

  const assignedIds = new Set(data?.resourceQueues?.map(({ id }) => id));

  const availableOptions = (selectData ?? []).filter((item) => !assignedIds.has(item.id));

  const cardConfig = {
    id: 'resource-queue-drawer-setup-cards',
    onDelete: (id) =>
      deleteResourceQueue({
        queueId: type === 'queue' ? id : data?.id,
        resourceId: type === 'resource' ? id : data?.id,
      }),
    onStatus: (record) => {
      updateResourceQueue({
        queueId: record.queueId,
        resourceId: record.resourceId,
        active: !record.active,
      });
    },
    fields: [{ name: 'queueStatus', label: 'Assignment' }],
  };

  /**
   * Handles changes to the selected queue or resource.
   * @param {React.ChangeEvent<HTMLSelectElement>} event - The change event object.
   * @returns {void}
   */
  const handleSelectionChange = ({ target: { value } }) => {
    if (value) setSelection(value);
  };

  /** * Handles the drawer close action. */
  const handleClose = () => {
    setSelection('');
    dispatch({ type: RESOURCE_QUEUE_SELECTED_ID, payload: '' });
  };

  /** * Handles the assign action. */
  const handleAssign = () => {
    setSelection('');
    /* v8 ignore next */
    if (data?.id)
      createResourceQueue({
        queueId: type === 'queue' ? selection : data.id,
        resourceId: type === 'resource' ? selection : data.id,
        queueStatus: 'PERMANENT',
      });
  };

  return (
    <Drawer direction={isMobile ? 'bottom' : 'right'} open={Boolean(data?.id)} onOpenChange={handleClose}>
      <DrawerContent titleText={`${type.toUpperCase()} ASSIGNMENTS`} showClose={false}>
        <div className={classes.content}>
          <div>{data?.description}</div>
          <div className={classes.selectContainer}>
            <Select
              className={classes.select}
              id='resource-queue-drawer-select'
              data-testid='resource-queue-drawer-select'
              labelText={`Select ${type}`}
              onChange={handleSelectionChange}
              value={selection}
              required
            >
              <option value=''>--- Select an Option ---</option>
              {availableOptions.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.description}
                </option>
              ))}
            </Select>
            <Button
              data-testid={`resource-queue-drawer-assign-${data?.id}`}
              className={classes.assignButton}
              color='primary'
              disabled={!selection}
              onClick={handleAssign}
            >
              ASSIGN
            </Button>
          </div>
          <SetupCards data={data?.resourceQueues} config={cardConfig} />
        </div>
        <div className={classes.drawerFooter}>
          <Button
            data-testid={`resource-queue-drawer-done-${data?.id}`}
            className={classes.doneButton}
            color='primary'
            onClick={handleClose}
          >
            DONE
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
